import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import {
  useCreateClientHouseholdMutation,
  useAddHouseholdMemberMutation,
  useDeleteClientUserMutation,
  useReactivateClientUserMutation,
  useListClientUsersQuery,
  useSetClientDocumentsMutation,
  useUpdateClientUserMutation,
  useCreateClientLoginMutation,
  useGetClientDocumentCatalogQuery,
  useGetAdminConfigQuery,
  useSendClientAgreementMutation,
  useLoadAdvisorsQuery,
  useListMyOfficesQuery,
  useLoadCompanyDirectoryQuery,
  useSetClientFscLinkMutation,
  useClearClientFscLinkMutation,
  useLazyGetFscHouseholdQuery,
  useLazyGetCrmClientQuery,
} from '../../app/api';
import ExistingClientSearch from './ExistingClientSearch';
import {
  useIsMobile, GEMSTONE_OPTIONS, DIAMOND_GEMSTONES,
  WORKFLOW_STAGE_OPTIONS, DEFAULT_WORKFLOW_STAGE,
} from '../utils';

const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  hairlineStrong: '#c4cbd4',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
  green:     '#1a7f4e',
  red:       '#b00020',
};

function firstNameOf(c) {
  return (c?.first_name || '').trim() || (c?.email || '').split('@')[0] || 'Client';
}

// ─── Lead status pill (same palette as before) ────────────────────────────────
const LEAD_STATUS_STYLE = {
  NEW:                  { bg: 'rgba(0,105,166,0.10)',   color: work.tealBlue, label: 'New' },
  OPEN:                 { bg: 'rgba(0,105,166,0.10)',   color: work.tealBlue, label: 'Open' },
  IN_PROGRESS:          { bg: 'rgba(208,155,44,0.18)',  color: work.goldenrod, label: 'In Progress' },
  ATTEMPTED_TO_CONTACT: { bg: 'rgba(208,155,44,0.18)',  color: work.goldenrod, label: 'Attempted' },
  CONNECTED:            { bg: 'rgba(26,127,78,0.12)',   color: work.green,    label: 'Connected' },
  OPEN_DEAL:            { bg: 'rgba(26,127,78,0.12)',   color: work.green,    label: 'Open Deal' },
  UNQUALIFIED:          { bg: 'rgba(176,0,32,0.08)',    color: work.red,      label: 'Unqualified' },
  BAD_TIMING:           { bg: 'rgba(176,0,32,0.08)',    color: work.red,      label: 'Bad Timing' },
};
function prettyLeadStatus(raw) {
  if (!raw) return null;
  if (LEAD_STATUS_STYLE[raw]) return LEAD_STATUS_STYLE[raw];
  const label = String(raw).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  return { bg: 'rgba(88,88,91,0.12)', color: work.muted, label };
}
function LeadStatusPill({ status }) {
  const s = prettyLeadStatus(status);
  if (!s) return null;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontWeight: 700, fontSize: '0.68rem',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      padding: '0.18rem 0.5rem', borderRadius: 4, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}
LeadStatusPill.propTypes = { status: PropTypes.string };

// ─── Copy button (reused for the temp-password readout at the end) ────────────
function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value);
      else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <button type="button" onClick={onClick} style={{
      background: copied ? work.green : work.tealBlue,
      color: 'white', border: 0, borderRadius: 4,
      padding: '0.35rem 0.7rem', fontWeight: 700, fontSize: '0.8rem',
      cursor: 'pointer', whiteSpace: 'nowrap',
    }}>
      <i className={`bi bi-${copied ? 'check-lg' : 'clipboard'} me-1`} />
      {copied ? 'Copied' : (label || 'Copy')}
    </button>
  );
}
CopyButton.propTypes = { value: PropTypes.string.isRequired, label: PropTypes.string };

// ─── Step indicator ──────────────────────────────────────────────────────────
const STEPS = [
  { id: 'household', label: 'Household' },
  { id: 'advisor',   label: 'Advisor' },
  { id: 'fee',       label: 'Fee' },
  { id: 'documents', label: 'Documents' },
  { id: 'login',     label: 'Login' },
];

// Household member associations. Values match VALID_ASSOCIATIONS on the backend
// (Salesforce relationship roles); order here is the dropdown order.
const ASSOCIATIONS = [
  { value: 'primary',          label: 'Primary' },
  { value: 'spouse',           label: 'Spouse' },
  { value: 'domestic_partner', label: 'Domestic Partner' },
  { value: 'child',            label: 'Child' },
  { value: 'parent',           label: 'Parent' },
  { value: 'sibling',          label: 'Sibling' },
  { value: 'dependent',        label: 'Dependent' },
  { value: 'beneficiary',      label: 'Beneficiary' },
  { value: 'trustee',          label: 'Trustee' },
  { value: 'power_of_attorney',label: 'Power of Attorney' },
  { value: 'executor',         label: 'Executor' },
  { value: 'business_partner', label: 'Business Partner' },
  { value: 'other',            label: 'Other' },
];

// Where the consistent top-bar "Back" button goes from each step. Advisor is the
// first step (no back); login-result is post-creation (nothing to undo).
const PREV_STEP = {
  advisor: 'household',
  fee: 'advisor',
  documents: 'fee',
  'login-prompt': 'documents',
};

function StepIndicator({ current, onBack }) {
  const currentIdx = STEPS.findIndex(s => s.id === current);
  return (
    <div style={{
      background: work.surface,
      border: `1px solid ${work.hairline}`,
      borderRadius: 6,
      padding: '0.7rem 1rem',
      marginBottom: '1.25rem',
      display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
    }}>
      {STEPS.map((s, i) => {
        const done = i < currentIdx;
        const here = i === currentIdx;
        return (
          <React.Fragment key={s.id}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: done ? work.green : here ? work.deepBlue : work.hairline,
                color: done || here ? 'white' : work.muted,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 800,
              }}>
                {done ? <i className="bi bi-check-lg" /> : i + 1}
              </span>
              <span style={{
                color: here ? work.ink : work.muted,
                fontWeight: here ? 800 : 600,
                fontSize: '0.78rem',
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <span style={{ color: work.hairlineStrong, fontSize: '0.8rem' }}>›</span>
            )}
          </React.Fragment>
        );
      })}
      {/* Back lives on the right of the step bar so it's in a consistent spot on
          every step. Hidden on the first step (nothing to go back to). */}
      {onBack && (
        <button type="button" onClick={onBack} style={{
          marginLeft: 'auto', background: 'transparent', color: work.deepBlue,
          border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
          padding: '0.35rem 0.75rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
        }}>
          <i className="bi bi-chevron-left me-1" />Back
        </button>
      )}
    </div>
  );
}
StepIndicator.propTypes = {
  current: PropTypes.oneOf(STEPS.map(s => s.id)).isRequired,
  onBack: PropTypes.func,
};

// ─── Point people picker ─────────────────────────────────────────────────────
// Internal staff notified when this household's clients upload documents or
// ask questions from the portal. Employees come from the company directory
// (type-ahead against the already-loaded list — no server round trip per
// keystroke); anyone else can be added as a raw email address.
function PointPeopleCard({ pointPeople, onAddEmployee, onAddEmail, onRemove, isMobile }) {
  const { data: directory = [] } = useLoadCompanyDirectoryQuery();
  const [query, setQuery] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState(null);

  const existingDirectoryIds = new Set(pointPeople.filter(p => p.kind === 'employee').map(p => p.directory_id));
  const existingEmails = new Set(pointPeople.map(p => (p.email || '').toLowerCase()));

  const q = query.trim().toLowerCase();
  const matches = q
    ? directory
        .filter(d => d.is_active !== false && !existingDirectoryIds.has(d.id))
        .filter(d => {
          const name = `${d.first_name || ''} ${d.last_name || ''}`.toLowerCase();
          return name.includes(q) || (d.final_email || '').toLowerCase().includes(q);
        })
        .slice(0, 8)
    : [];

  const pickEmployee = (d) => {
    onAddEmployee(d);
    setQuery('');
  };

  const addEmail = () => {
    const email = emailDraft.trim().toLowerCase();
    if (!email || !email.includes('@')) { setEmailError('Enter a valid email.'); return; }
    if (existingEmails.has(email)) { setEmailError('Already added.'); return; }
    onAddEmail(email);
    setEmailDraft('');
    setEmailError(null);
  };

  const labelStyle = { display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 };
  const inputStyle = { width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, background: 'white', outline: 'none' };

  return (
    <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6, padding: '1.25rem 1.4rem', width: isMobile ? '100%' : 320, flexShrink: 0 }}>
      <div style={{ fontWeight: 800, color: work.deepBlue, fontSize: '1rem', marginBottom: '0.15rem' }}>Point People</div>
      <p style={{ color: work.muted, fontSize: '0.85rem', margin: '0 0 0.9rem' }}>
        Notified by email when this client uploads documents or asks a question from the portal.
      </p>

      <label style={labelStyle}>Add an employee</label>
      <div style={{ position: 'relative', marginBottom: '0.9rem' }}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email…"
          style={inputStyle}
        />
        {q && (
          <div style={{
            position: 'absolute', zIndex: 5, top: '100%', left: 0, right: 0, marginTop: 2,
            background: 'white', border: `1px solid ${work.hairline}`, borderRadius: 4,
            boxShadow: '0 4px 10px rgba(10,41,71,0.12)', maxHeight: 220, overflowY: 'auto',
          }}>
            {matches.length === 0 ? (
              <div style={{ padding: '0.6rem 0.7rem', color: work.muted, fontSize: '0.82rem' }}>No matches.</div>
            ) : matches.map(d => (
              <div
                key={d.id}
                onMouseDown={() => pickEmployee(d)}
                style={{ padding: '0.5rem 0.7rem', cursor: 'pointer', borderBottom: `1px solid ${work.hairline}` }}
                onMouseEnter={(e) => { e.currentTarget.style.background = work.canvas; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
              >
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: work.ink }}>{`${d.first_name || ''} ${d.last_name || ''}`.trim()}</div>
                <div style={{ fontSize: '0.72rem', color: work.muted }}>{d.final_email}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <label style={labelStyle}>Or add an email address</label>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <input
          type="email"
          value={emailDraft}
          onChange={(e) => { setEmailDraft(e.target.value); setEmailError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
          placeholder="name@example.com"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button
          type="button"
          onClick={addEmail}
          disabled={!emailDraft.trim()}
          style={{
            background: !emailDraft.trim() ? work.hairline : work.tealBlue,
            color: !emailDraft.trim() ? work.muted : 'white',
            border: 0, borderRadius: 4, padding: '0 0.9rem', fontWeight: 700, fontSize: '0.85rem',
            cursor: !emailDraft.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          Add
        </button>
      </div>
      {emailError && <div style={{ color: work.red, fontSize: '0.75rem', marginTop: 4 }}>{emailError}</div>}

      {pointPeople.length > 0 && (
        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {pointPeople.map(p => (
            <div key={p.uid} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
              background: work.canvas, borderRadius: 4, padding: '0.4rem 0.6rem',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: work.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <i className={`bi ${p.kind === 'employee' ? 'bi-person-badge' : 'bi-envelope'} me-1`} />
                  {p.kind === 'employee' ? p.name : p.email}
                </div>
                {p.kind === 'employee' && (
                  <div style={{ fontSize: '0.72rem', color: work.muted }}>{p.email}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => onRemove(p.uid)}
                aria-label={`Remove ${p.kind === 'employee' ? p.name : p.email}`}
                style={{ background: 'none', border: 0, padding: 0, color: work.muted, cursor: 'pointer', flexShrink: 0 }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
PointPeopleCard.propTypes = {
  pointPeople: PropTypes.array.isRequired,
  onAddEmployee: PropTypes.func.isRequired,
  onAddEmail: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

// ─── Main wizard ─────────────────────────────────────────────────────────────
export default function ClientNew() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [step, setStep] = useState('household'); // household | advisor | fee | documents | login-prompt | login-result

  // Step 1 — advisor(s) for the household, picked in the wizard itself.
  const { data: advisorList = [] } = useLoadAdvisorsQuery();
  const [advisorName, setAdvisorName] = useState('');
  const [advisorName2, setAdvisorName2] = useState('');
  // Second advisor is hidden by default; it auto-appears (required) for a
  // diamond-type gemstone, but can also be added manually for any tier.
  const [showSecondAdvisor, setShowSecondAdvisor] = useState(false);
  const [gemstone, setGemstone] = useState('');
  // The FSC office this household is filed at — what decides which colleagues
  // can see it at all (work office scope). Only the caller's own offices are
  // offered; with exactly one, `prefill` answers it and the card shows the
  // name rather than a pointless one-item dropdown.
  const { data: myOffices, isLoading: officesLoading } = useListMyOfficesQuery();
  const officeOptions = myOffices?.offices || [];
  const [officeFscId, setOfficeFscId] = useState('');
  useEffect(() => {
    if (myOffices?.prefill) setOfficeFscId(myOffices.prefill);
  }, [myOffices?.prefill]);
  // Where the household starts in the four-stage process. Almost always
  // Discovery for a brand-new client, but a household being migrated in
  // mid-relationship can start further along — this is what lights up the
  // progress bar the client sees on their portal dashboard.
  const [stage, setStage] = useState(DEFAULT_WORKFLOW_STAGE);
  // Beta/UAT household for third-party testing before go-live — never a
  // real client. Routes uploads to a separate, deletable S3 bucket instead
  // of the production one (which is WORM-locked and can't be cleaned up).
  const [isTest, setIsTest] = useState(false);
  const isDiamondGem = DIAMOND_GEMSTONES.has(gemstone);
  const secondAdvisorVisible = isDiamondGem || showSecondAdvisor;
  useEffect(() => {
    if (!secondAdvisorVisible) setAdvisorName2('');
  }, [secondAdvisorVisible]);

  // Point people — internal staff notified on client uploads/questions.
  // Held locally like household members and sent with the create-household
  // call; `uid` is a stable React key for unsaved rows.
  const [pointPeople, setPointPeople] = useState([]);
  const pointPersonUid = useRef(0);
  const addPointEmployee = (d) => setPointPeople(prev => [...prev, {
    uid: `pp${pointPersonUid.current++}`, kind: 'employee',
    directory_id: d.id, name: `${d.first_name || ''} ${d.last_name || ''}`.trim(),
    email: d.final_email,
  }]);
  const addPointEmail = (email) => setPointPeople(prev => [...prev, {
    uid: `pp${pointPersonUid.current++}`, kind: 'email', email,
  }]);
  const removePointPerson = (uid) => setPointPeople(prev => prev.filter(p => p.uid !== uid));

  // Household under construction (Step 1). Members are held locally until the
  // operator hits Continue, at which point the whole household is created in one
  // request. `uid` is a stable React key for unsaved rows.
  const [householdName, setHouseholdName] = useState('');
  // Optional tie to the FSC household this portal household already is, for
  // an existing AUM client being given portal access. Held locally and sent
  // with the create call; `linkedFscId` is what the server currently holds, so
  // a Back → change → Continue can tell "unchanged" from "move it".
  const [fscHousehold, setFscHousehold] = useState(null);
  const [linkedFscId, setLinkedFscId] = useState(null);
  // What office the server currently holds, same idea as `linkedFscId`: a
  // Back → change the office → Continue has to move the household, and an
  // untouched Back → Continue must not cost a write.
  const [savedOfficeFscId, setSavedOfficeFscId] = useState(null);
  // Linking an FSC (AUM) household settles the office on its own: that record
  // is authoritative about whose client this is, and the server applies the
  // same precedence, so the Office control steps aside rather than asking for
  // an answer it would then ignore.
  const officeFromFsc = Boolean(fscHousehold?.rep_firm_name);
  // Each member: {uid, public_id?, first_name, last_name, email, phone, association}.
  // public_id is set once the member exists on the server (after creation), which
  // lets Back → edit reconcile updates/adds/deletes.
  const [members, setMembers] = useState([]);
  const memberUid = useRef(0);
  const toLocalMember = (m) => ({
    uid: `m${memberUid.current++}`,
    public_id: m.public_id || undefined,
    first_name: m.first_name || '', last_name: m.last_name || '',
    email: m.email || '', phone: m.phone || '', association: m.association || 'other',
    hubspot_contact_id: m.hubspot_contact_id || undefined,
  });
  const removeMember = (uid) => setMembers(prev => prev.filter(m => m.uid !== uid));
  const addMember = (m) => setMembers(prev => [...prev, { ...toLocalMember(m) }]);
  const updateMember = (uid, patch) => setMembers(prev => prev.map(m => (m.uid === uid ? { ...m, ...patch } : m)));

  // Persisted household after Step 1. `primary` drives the documents/login
  // steps (checklist + logins attach via public_id); `created` is every member.
  const [primary, setPrimary] = useState(null);
  const [created, setCreated] = useState([]); // all created members (with public_id)
  const [creatingHousehold, setCreatingHousehold] = useState(false);

  // Fee step — whether a one-time discovery fee applies. Defaulted to No and
  // "Yes" disabled for now (coming soon) — re-enable once the fee flow is
  // ready to turn back on.
  const [applyFee, setApplyFee] = useState(false);
  const [fee, setFee] = useState('');
  const [savingFee, setSavingFee] = useState(false);

  // One document checklist for the whole household — the client uploads for
  // everyone as needed, so we no longer split the list per person.
  const [selDocs, setSelDocs] = useState(new Set());
  // Ad-hoc "custom" requests: free-text descriptions of anything not in the
  // catalog. The operator can add as many as they need; each becomes its own
  // tracked request. `uid` is just a stable React key for unsaved rows.
  const [customList, setCustomList] = useState([]);
  const customUid = useRef(0);
  const addCustom = () => setCustomList(prev => [...prev, { label: '', uid: `c${customUid.current++}` }]);
  const changeCustom = (uid, label) => setCustomList(prev => prev.map(c => (c.uid === uid ? { ...c, label } : c)));
  const removeCustom = (uid) => setCustomList(prev => prev.filter(c => c.uid !== uid));
  const [error, setError] = useState(null);
  // { client_public_id, name, message } when a create/add hits a deactivated
  // client's email — offers reactivating that client instead of a dead end.
  const [emailConflict, setEmailConflict] = useState(null);
  // [{ id, email, temp_password, email_sent, email_error }]
  const [generatedLogins, setGeneratedLogins] = useState([]);
  // Whether to also email each client their temp password + portal link.
  const [emailClient, setEmailClient] = useState(true);

  const { data: catalog = [] } = useGetClientDocumentCatalogQuery();
  const { data: existingClients = [] } = useListClientUsersQuery();
  // Admin toggle: whether the "prefill from HubSpot" search shows in this flow.
  // Defaults to on until the config loads / if the call fails.
  const { data: adminConfig } = useGetAdminConfigQuery();
  const showHubspot = adminConfig?.config?.show_hubspot_client_import !== false;
  // Admin toggle: whether the "test household" checkbox shows at all. When
  // turned off mid-session, force isTest back off too — otherwise a
  // previously-checked box could still submit is_test=true after the option
  // disappears from view.
  const showTestOption = adminConfig?.config?.show_test_household_option !== false;
  useEffect(() => {
    if (!showTestOption) setIsTest(false);
  }, [showTestOption]);
  const [createHousehold] = useCreateClientHouseholdMutation();
  const [addHouseholdMember] = useAddHouseholdMemberMutation();
  const [deleteClient] = useDeleteClientUserMutation();
  const [reactivateClient, { isLoading: isReactivatingConflict }] = useReactivateClientUserMutation();
  const [setDocs] = useSetClientDocumentsMutation();
  const [updateClient] = useUpdateClientUserMutation();
  const [createLogin] = useCreateClientLoginMutation();
  const [sendAgreement] = useSendClientAgreementMutation();
  const [setFscLink] = useSetClientFscLinkMutation();
  const [clearFscLink] = useClearClientFscLinkMutation();

  // Selecting an existing FSC client fills in what we already know about them,
  // but only where the operator hasn't typed something themselves — a prefill
  // that overwrites a deliberate entry is worse than no prefill.
  const chooseFscHousehold = (h) => {
    setFscHousehold(h);
    if (!householdName.trim() && h?.name) setHouseholdName(h.name);
    if (!advisorName && h?.rep_name && (advisorList || []).some(a => a.name === h.rep_name)) {
      setAdvisorName(h.rep_name);
    }
  };

  // "Existing client?" picks a HOUSEHOLD as the operator thinks of it: a
  // Salesforce household and/or the firm-CRM people in it, already combined by
  // ExistingClientSearch. The Salesforce household is linked (saved with the
  // portal household); the CRM side is prefill only - the household name, the
  // primary member, a spouse/partner, and the advisor, each only where still
  // blank. Everyone else in the CRM household is offered under the pick.
  const [crmClient, setCrmClient] = useState(null);
  // The Salesforce household the picked CRM person is matched to, so a
  // mismatch with the linked Salesforce client can name it.
  const [crmMatchedHousehold, setCrmMatchedHousehold] = useState(null);
  // The rest of the picked person's household in their CRM (spouse, kids…).
  const [crmHousehold, setCrmHousehold] = useState([]);
  // The HubSpot lead in the picked row, shown under the pick.
  const [hubspotLead, setHubspotLead] = useState(null);
  const [fetchCrmClient] = useLazyGetCrmClientQuery();
  const [fetchFscHousehold] = useLazyGetFscHouseholdQuery();
  const canUseAdvisor = (name) => name && (advisorList || []).some(a => a.name === name);

  const chooseExisting = async ({ fsc, people = [], hubspot = null }) => {
    const primary = pickPrimaryPerson(people);
    setHubspotLead(hubspot);
    // A HubSpot lead nobody else knows: the lead IS the primary. The contact
    // id is saved on the member, so the tie back to HubSpot survives.
    if (!fsc && !primary && hubspot) {
      if (!householdName.trim()) {
        setHouseholdName(hubspot.last_name ? `The ${hubspot.last_name} Family`
          : [hubspot.first_name, hubspot.last_name].filter(Boolean).join(' '));
      }
      if (!members.some(m => m.association === 'primary')) {
        addMember({
          first_name: hubspot.first_name, last_name: hubspot.last_name, email: hubspot.email,
          phone: hubspot.phone, association: 'primary', hubspot_contact_id: hubspot.hubspot_contact_id,
        });
      }
      return;
    }
    // One name decision, made here: Salesforce's ("Bruce & Jolene Aardsma")
    // over the CRM's ("The Aardsma Family"), never over what was typed.
    if (!householdName.trim()) {
      setHouseholdName(fsc?.name || primary?.household_name || primary?.display_name || '');
    }
    if (!advisorName) {
      const adv = [fsc?.rep_name, primary?.advisor_name].find(canUseAdvisor);
      if (adv) setAdvisorName(adv);
    }
    if (fsc) setFscHousehold(fsc);
    setCrmMatchedHousehold(fsc && primary?.fsc_household_id === fsc.fsc_id ? fsc : null);
    setCrmClient(primary || null);
    setCrmHousehold([]);
    if (!primary) return;

    const usedEmails = new Set([...existingEmails, ...members.map(m => (m.email || '').toLowerCase())].filter(Boolean));
    if (!members.some(m => m.association === 'primary')) {
      addMember({
        first_name: primary.first_name, last_name: primary.last_name,
        email: primary.email, phone: primary.phone, association: 'primary',
        // The same person as a HubSpot lead (matched on email in the search).
        hubspot_contact_id: hubspot && (hubspot.email || '').toLowerCase() === (primary.email || '').toLowerCase()
          ? hubspot.hubspot_contact_id : undefined,
      });
      if (primary.email) usedEmails.add(primary.email.toLowerCase());
    }

    const [{ data: detail }, matched] = await Promise.all([
      fetchCrmClient({ id: primary.id }),
      !fsc && primary.fsc_household_id ? fetchFscHousehold({ id: primary.fsc_household_id }) : Promise.resolve({}),
    ]);
    const others = (detail?.household || []).filter(h => h.id !== primary.id);
    setCrmHousehold(others);
    // A spouse/partner comes along automatically - that is the household the
    // operator picked. Children, beneficiaries etc. stay one click away.
    const haveNames = new Set(members.map(m => `${m.first_name}|${m.last_name}`.toLowerCase()));
    others.forEach(o => {
      const assoc = guessAssociation(o.status);
      if (!['spouse', 'domestic_partner'].includes(assoc) || /deceased/i.test(o.status || '')) return;
      if (haveNames.has(`${o.first_name}|${o.last_name}`.toLowerCase())) return;
      const em = (o.email || '').trim();
      addMember({
        first_name: o.first_name, last_name: o.last_name, phone: o.phone, association: assoc,
        email: em && !usedEmails.has(em.toLowerCase()) ? em : '',
      });
    });
    if (matched?.data) {
      setCrmMatchedHousehold(matched.data);
      // Functional: a Salesforce client linked while this fetch was in flight wins.
      setFscHousehold(prev => prev || matched.data);
    }
  };

  const existingEmails = new Set(
    existingClients.map(c => (c.email || '').toLowerCase()).filter(Boolean)
  );

  const hasPrimary = members.some(m => m.association === 'primary');
  // Boolean() matters: `''.trim() && …` short-circuits to the empty STRING, not
  // false, so without it this prop is a string whenever the name is blank.
  const canContinueHousehold = Boolean(householdName.trim()) && members.length > 0 && hasPrimary;
  // An office is required unless the FSC link will supply one, or the caller
  // is full-access (the only scope allowed to file a client at no office —
  // see /clients/my_offices.can_file_unassigned). Same rule the server
  // enforces, so the button greys out instead of collecting a 403.
  const officeNeeded = !officeFromFsc && !myOffices?.can_file_unassigned;
  const canSaveHousehold = Boolean(advisorName)
    && !(isDiamondGem && !advisorName2)
    && !(officeNeeded && !officeFscId)
    && !creatingHousehold;

  const handleCreateHousehold = async () => {
    setError(null);
    setEmailConflict(null);
    if (!canContinueHousehold) return;
    if (members.filter(m => m.association === 'primary').length !== 1) {
      setError('Exactly one member must be the primary.');
      return;
    }
    if (officeNeeded && !officeFscId) {
      setError('Pick an office for this household.');
      return;
    }
    setCreatingHousehold(true);
    try {
      if (!primary) {
        // First time: create the whole household in one call, then hydrate the
        // local members with their server public_ids so a later Back → edit can
        // reconcile.
        const res = await createHousehold({
          household_name: householdName.trim(),
          advisor_name: advisorName || undefined,
          advisor_name_2: advisorName2 || undefined,
          gemstone: gemstone || undefined,
          workflow_status: stage || undefined,
          fsc_household_id: fscHousehold?.fsc_id || undefined,
          office_fsc_id: officeFscId || undefined,
          is_test: isTest,
          point_people: pointPeople.map(p => ({
            kind: p.kind,
            directory_id: p.kind === 'employee' ? p.directory_id : undefined,
            email: p.kind === 'email' ? p.email : undefined,
          })),
          members: members.map(m => ({
            first_name: m.first_name, last_name: m.last_name,
            email: m.email || undefined, phone: m.phone || undefined,
            association: m.association,
            hubspot_contact_id: m.hubspot_contact_id || undefined,
          })),
        }).unwrap();
        const local = (res.members || []).map(toLocalMember);
        setMembers(local);
        setCreated(local);
        setPrimary(res.primary || local.find(m => m.association === 'primary') || null);
        // The create call linked it in the same request (or didn't) — record
        // what the server now holds so a later Back → edit only sends a change.
        setLinkedFscId(fscHousehold?.fsc_id || null);
        setSavedOfficeFscId(officeFscId || null);
      } else {
        // Already created (Back → edit): reconcile the diff against the server.
        const anchor = members.find(m => m.public_id) || primary;
        // Deletes: previously-created members no longer in the list.
        const keptIds = new Set(members.filter(m => m.public_id).map(m => m.public_id));
        for (const c of created) {
          if (c.public_id && !keptIds.has(c.public_id)) {
            await deleteClient({ id: c.public_id }).unwrap();
          }
        }
        // Updates (existing) + adds (new), building the final list.
        const finalMembers = [];
        for (const m of members) {
          if (m.public_id) {
            await updateClient({
              id: m.public_id,
              first_name: m.first_name, last_name: m.last_name,
              email: m.email || '', phone: m.phone || '', association: m.association,
              // Picked up here too so Back → change the stage → Continue
              // actually moves the household, not just the first pass.
              workflow_status: stage,
            }).unwrap();
            finalMembers.push({ ...m });
          } else {
            const createdMember = await addHouseholdMember({
              primary_public_id: anchor.public_id,
              member: {
                first_name: m.first_name, last_name: m.last_name,
                email: m.email || undefined, phone: m.phone || undefined,
                association: m.association, hubspot_contact_id: m.hubspot_contact_id || undefined,
              },
            }).unwrap();
            // A new member inherits the anchor's stage, which is stale if the
            // stage was changed on this pass and the anchor hasn't been
            // updated yet — put it right rather than depend on loop order.
            if (createdMember?.workflow_status !== stage) {
              await updateClient({ id: createdMember.public_id, workflow_status: stage }).unwrap();
            }
            finalMembers.push({ ...toLocalMember(createdMember), uid: m.uid });
          }
        }
        setMembers(finalMembers);
        setCreated(finalMembers);
        setPrimary(finalMembers.find(m => m.association === 'primary') || primary);

        // An office change moves the whole household in one call (the PATCH
        // writes every member), so it goes to the primary only. Before the
        // FSC link below, which overrides the office when it changes.
        if (!officeFromFsc && officeFscId && officeFscId !== savedOfficeFscId) {
          const officeTarget = (finalMembers.find(m => m.association === 'primary') || primary).public_id;
          await updateClient({ id: officeTarget, office_fsc_id: officeFscId }).unwrap();
          setSavedOfficeFscId(officeFscId);
        }

        // The household already exists, so the FSC link goes through its own
        // endpoint rather than the create payload. Only on an actual change,
        // so Back → Continue with nothing touched costs no extra request.
        const wanted = fscHousehold?.fsc_id || null;
        if (wanted !== linkedFscId) {
          const linkTarget = (finalMembers.find(m => m.association === 'primary') || primary).public_id;
          if (wanted) await setFscLink({ id: linkTarget, fsc_household_id: wanted }).unwrap();
          else await clearFscLink({ id: linkTarget }).unwrap();
          setLinkedFscId(wanted);
        }
      }
      setStep('fee');
    } catch (e) {
      const detail = e?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'email_deactivated') {
        setEmailConflict(detail);
      } else if (detail && typeof detail === 'object') {
        // Other structured details (an FSC household already claimed, say)
        // carry their own message — render that, never the object itself.
        setError(detail.message || 'Could not save the household.');
      } else {
        setError(detail || 'Could not save the household.');
      }
    } finally {
      setCreatingHousehold(false);
    }
  };

  const handleReactivateConflict = async () => {
    if (!emailConflict) return;
    try {
      await reactivateClient({ id: emailConflict.client_public_id }).unwrap();
      navigate(`/work/clients/${emailConflict.client_public_id}`);
    } catch (e) {
      setEmailConflict(null);
      setError(e?.data?.detail || 'Could not reactivate that client.');
    }
  };

  const handleSaveFeeAndContinue = async () => {
    setError(null);
    // No discovery fee → straight to documents; no fee saved, no agreement.
    if (!applyFee) { setStep('documents'); return; }
    setSavingFee(true);
    try {
      const dollars = Number((fee || '').replace(/[$,]/g, '').trim());
      if (!Number.isFinite(dollars) || dollars <= 0) {
        setError('Enter the discovery fee amount, or choose “No”.');
        setSavingFee(false);
        return;
      }
      await updateClient({ id: primary.public_id, onetime_fee_cents: Math.round(dollars * 100) }).unwrap();
      // Auto-generate + send the DocuSign agreement. Best-effort: if it can't
      // send now (e.g. DocuSign not configured), the advisor can send it from
      // the client's detail page, so we don't block the wizard.
      try { await sendAgreement({ id: primary.public_id }).unwrap(); }
      catch { /* fallback: "Send for signature" on the client detail page */ }
      setStep('documents');
    } catch (e) {
      setError(e?.data?.detail || 'Could not save the fee.');
    } finally {
      setSavingFee(false);
    }
  };

  const toggleDoc = (key) => {
    setSelDocs(prev => {
      const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
    });
  };

  const handleSaveDocsAndContinue = async () => {
    setError(null);
    try {
      // The checklist lives on the primary — the household uploads against this
      // single list. Send every custom row (backend drops blanks + inserts).
      await setDocs({
        id: primary.public_id,
        type_keys: Array.from(selDocs),
        custom_requests: customList.map(c => ({ label: c.label.trim() })),
      }).unwrap();
      setStep('login-prompt');
    } catch (e) {
      setError(e?.data?.detail || 'Could not save the document list.');
    }
  };

  // Every household member with an email can be issued a login. Name-only
  // members (beneficiaries, dependents…) stay Draft — the primary uploads on
  // their behalf.
  const loginableMembers = created.filter(c => c && c.email);

  const handleCreateLogins = async () => {
    setError(null);
    const out = [];
    try {
      for (const c of loginableMembers) {
        const res = await createLogin({ id: c.public_id, send_email: emailClient }).unwrap();
        out.push({
          id: c.public_id, email: res.email, temp_password: res.temp_password,
          email_sent: res.email_sent, email_error: res.email_error,
        });
      }
      setGeneratedLogins(out);
      setStep('login-result');
    } catch (e) {
      setError(e?.data?.detail || 'Could not generate login.');
    }
  };

  const skipLogin = () => navigate(`/work/clients/${primary.public_id}`);
  const finishToDetail = () => navigate(`/work/clients/${primary.public_id}`);

  return (
    <div style={{ background: work.canvas, minHeight: '100vh', padding: isMobile ? '1rem' : '1.5rem 1.75rem', color: work.ink }}>

      <Link to="/work/clients" style={{ color: work.tealBlue, fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
        <i className="bi bi-chevron-left" /> Portal clients
      </Link>

      {/* Header */}
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`,
        borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6,
        padding: isMobile ? '1.1rem 1.1rem' : '1.4rem 1.6rem',
        marginTop: '0.75rem', marginBottom: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap' }}>
          <div style={{
            background: work.deepBlue, color: 'white', width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6, flexShrink: 0,
          }}>
            <i className="bi bi-person-plus-fill" style={{ fontSize: '1.3rem' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: work.muted, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 2px' }}>
              Wealth Management
            </p>
            <h3 style={{ color: work.ink, fontWeight: 800, margin: 0, fontSize: isMobile ? '1.2rem' : '1.4rem', lineHeight: 1.15 }}>
              Set up a new portal client
            </h3>
            <p style={{ color: work.muted, fontSize: '0.85rem', margin: '0.35rem 0 0' }}>
              {step === 'household' && 'Name the household, then add the primary and anyone else who belongs to it.'}
              {step === 'advisor' && 'Choose the stage, gemstone tier, advisor (and a second advisor, if applicable), and point people. Saving happens at the end of this step.'}
              {step === 'fee' && 'The discovery fee is coming soon — continue without one for now.'}
              {step === 'documents' && 'Check every document we need from this client.'}
              {step === 'login-prompt' && 'Documents saved. Create logins now or leave it for later.'}
              {step === 'login-result' && 'Copy the temp password(s) — they’re only shown here.'}
            </p>
          </div>
        </div>
      </div>

      <StepIndicator
        current={(step === 'login-prompt' || step === 'login-result') ? 'login' : step}
        onBack={PREV_STEP[step] ? () => setStep(PREV_STEP[step]) : undefined}
      />

      {error && (
        <div style={{ background: 'rgba(176,0,32,0.06)', border: `1px solid ${work.red}`, color: work.red, borderRadius: 6, padding: '0.55rem 0.85rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {emailConflict && (
        <div style={{ background: 'rgba(176,0,32,0.06)', border: `1px solid ${work.red}`, borderRadius: 6, padding: '0.75rem 0.95rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
          <div style={{ color: work.red, marginBottom: '0.5rem' }}>
            <strong>{emailConflict.name}</strong> is a deactivated client — that email belongs to them.
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleReactivateConflict}
              disabled={isReactivatingConflict}
              style={{
                background: work.tealBlue, color: 'white', border: 0, borderRadius: 4,
                padding: '0.4rem 0.8rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
              }}
            >
              <i className="bi bi-arrow-counterclockwise me-1" />
              {isReactivatingConflict ? 'Reactivating…' : 'Reactivate them instead'}
            </button>
            <button
              type="button"
              onClick={() => setEmailConflict(null)}
              style={{
                background: 'transparent', color: work.deepBlue,
                border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                padding: '0.4rem 0.8rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
              }}
            >
              Use a different email
            </button>
          </div>
        </div>
      )}

      {/* STEP 1: gemstone + advisor selection */}
      {step === 'advisor' && (
        <>
          {/* Test-household toggle — routes uploads to a separate, deletable
              S3 bucket instead of the WORM-locked production one. Styled to
              stand out since it's easy to forget and hard to notice later.
              Hidden entirely when the admin config option is off. */}
          {showTestOption && (
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer',
              background: isTest ? 'rgba(208,155,44,0.10)' : work.surface,
              border: `1.5px solid ${isTest ? work.goldenrod : work.hairline}`,
              borderRadius: 6, padding: '0.85rem 1.1rem', marginBottom: '1rem', maxWidth: 900,
            }}>
              <input
                type="checkbox"
                checked={isTest}
                onChange={(e) => setIsTest(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <span>
                <span style={{ display: 'block', fontWeight: 800, color: isTest ? work.goldenrod : work.ink, fontSize: '0.9rem' }}>
                  <i className="bi bi-flask me-1" />This is a test household (beta testing only)
                </span>
                <span style={{ display: 'block', color: work.muted, fontSize: '0.8rem', marginTop: 2 }}>
                  Not a real client. Document uploads go to a separate storage bucket that can actually be deleted before go-live — the production bucket cannot (documents there are locked for 7 years for compliance).
                </span>
              </span>
            </label>
          )}

          {/* Stage strip — full width above the three cards, because it's the
              one thing here the client sees for themselves (it lights the
              progress bar on their portal dashboard). */}
          <div style={{
            background: work.surface, border: `1px solid ${work.hairline}`,
            borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6,
            padding: '1.1rem 1.4rem', marginBottom: '1rem',
          }}>
            <div style={{ fontWeight: 800, color: work.deepBlue, fontSize: '1rem', marginBottom: '0.15rem' }}>Stage</div>
            <p style={{ color: work.muted, fontSize: '0.85rem', margin: '0 0 0.9rem' }}>
              Where this household is in the process. New clients normally start at Discovery - this is the step the client sees lit up on their portal dashboard.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
              gap: '0.6rem',
            }}>
              {WORKFLOW_STAGE_OPTIONS.map((s, i) => {
                const selected = stage === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setStage(s.key)}
                    aria-pressed={selected}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.55rem', textAlign: 'left',
                      border: `1.5px solid ${selected ? s.color : work.hairline}`,
                      background: selected ? 'rgba(0,105,166,0.06)' : 'white',
                      borderRadius: 6, padding: '0.6rem 0.75rem', cursor: 'pointer',
                    }}
                  >
                    <span style={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                      background: selected ? s.color : work.hairline,
                      color: selected ? 'white' : work.muted,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.7rem', fontWeight: 800,
                    }}>{i + 1}</span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 800, color: selected ? work.ink : work.muted }}>
                        {s.label}
                      </span>
                      <span style={{ display: 'block', fontSize: '0.75rem', color: work.muted, lineHeight: 1.25 }}>
                        {s.hint}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'stretch' }}>
          {/* Gemstone card (left) */}
          <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6, padding: '1.25rem 1.4rem', width: isMobile ? '100%' : 300, flexShrink: 0 }}>
            <div style={{ fontWeight: 800, color: work.deepBlue, fontSize: '1rem', marginBottom: '0.15rem' }}>Gemstone</div>
            <p style={{ color: work.muted, fontSize: '0.85rem', margin: '0 0 1rem' }}>
              Household tier. Diamond tiers require a second advisor.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.6rem' }}>
              {GEMSTONE_OPTIONS.map(g => {
                const selected = gemstone === g.key;
                return (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => setGemstone(selected ? '' : g.key)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem',
                      border: `1.5px solid ${selected ? work.tealBlue : work.hairline}`,
                      background: selected ? 'rgba(0,105,166,0.06)' : 'white',
                      borderRadius: 6, padding: '0.65rem 0.4rem', cursor: 'pointer',
                    }}
                  >
                    <i className="bi bi-suit-diamond-fill" style={{ fontSize: '1.5rem', color: g.color }} />
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: work.ink, textAlign: 'center', lineHeight: 1.15 }}>{g.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Advisor card (right) */}
          <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6, padding: '1.25rem 1.4rem', flex: '1 1 320px', maxWidth: 400 }}>
            <div style={{ fontWeight: 800, color: work.deepBlue, fontSize: '1rem', marginBottom: '0.15rem' }}>Advisor</div>
            <p style={{ color: work.muted, fontSize: '0.85rem', margin: '0 0 1rem' }}>
              Who is the advisor for this client? This is recorded on the client and drives who signs the agreement.
            </p>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Advisor</label>
            <select
              value={advisorName}
              onChange={(e) => setAdvisorName(e.target.value)}
              style={{ width: '100%', maxWidth: 360, border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, background: 'white', outline: 'none' }}
            >
              <option value="">Select an advisor…</option>
              {(advisorList || []).map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
              {advisorName && !(advisorList || []).some(a => a.name === advisorName) && (
                <option value={advisorName}>{advisorName}</option>
              )}
            </select>

            {secondAdvisorVisible ? (
              <>
                <label style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '1rem 0 4px' }}>
                  Second advisor{isDiamondGem ? ' (required for diamond tiers)' : ' (optional)'}
                </label>
                <select
                  value={advisorName2}
                  onChange={(e) => setAdvisorName2(e.target.value)}
                  style={{ width: '100%', maxWidth: 360, border: `1px solid ${isDiamondGem && !advisorName2 ? work.red : work.hairline}`, borderRadius: 4, padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, background: 'white', outline: 'none' }}
                >
                  <option value="">{isDiamondGem ? 'Select a second advisor…' : 'None'}</option>
                  {(advisorList || []).filter(a => a.name !== advisorName).map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
                {!isDiamondGem && (
                  <button
                    type="button"
                    onClick={() => setShowSecondAdvisor(false)}
                    style={{ display: 'block', marginTop: '0.4rem', background: 'none', border: 0, padding: 0, color: work.muted, fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Remove second advisor
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setShowSecondAdvisor(true)}
                style={{ display: 'block', marginTop: '0.9rem', background: 'none', border: 0, padding: 0, color: work.tealBlue, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
              >
                <i className="bi bi-plus-lg me-1" />Add a second advisor
              </button>
            )}

            {/* Office — who can SEE this household, as distinct from who
                advises it. Kept in the advisor card because both answer
                "whose client is this", and this is the step that saves. */}
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '1rem 0 4px' }}>
              Office
            </label>
            {officeFromFsc ? (
              <div style={{ fontSize: '0.88rem', color: work.ink }}>
                {fscHousehold.rep_firm_name}
                <div style={{ color: work.muted, fontSize: '0.78rem', marginTop: 2 }}>
                  From the linked AUM household.
                </div>
              </div>
            ) : officesLoading ? (
              <div style={{ color: work.muted, fontSize: '0.85rem' }}>Loading offices…</div>
            ) : officeOptions.length === 1 && !myOffices?.can_file_unassigned ? (
              <div style={{ fontSize: '0.88rem', color: work.ink }}>{officeOptions[0].name}</div>
            ) : officeOptions.length === 0 ? (
              <div style={{ color: work.red, fontSize: '0.82rem' }}>
                You have no assigned offices, so you cannot file a client. Ask a Super Admin
                for office access on the Work Access page.
              </div>
            ) : (
              <select
                value={officeFscId}
                onChange={(e) => setOfficeFscId(e.target.value)}
                style={{ width: '100%', maxWidth: 360, border: `1px solid ${officeNeeded && !officeFscId ? work.red : work.hairline}`, borderRadius: 4, padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, background: 'white', outline: 'none' }}
              >
                <option value="">
                  {myOffices?.can_file_unassigned ? 'No office - full-access users only' : 'Select an office…'}
                </option>
                {officeOptions.map(o => <option key={o.fsc_id} value={o.fsc_id}>{o.name}</option>)}
              </select>
            )}
            {!officeFromFsc && officeOptions.length > 0 && (
              <div style={{ color: work.muted, fontSize: '0.78rem', marginTop: 4 }}>
                Decides which colleagues can see this household. Linking a Salesforce client later
                moves it to that client&apos;s office.
              </div>
            )}
          </div>

          {/* Point people card (right of Advisor) */}
          <PointPeopleCard
            pointPeople={pointPeople}
            onAddEmployee={addPointEmployee}
            onAddEmail={addPointEmail}
            onRemove={removePointPerson}
            isMobile={isMobile}
          />
        </div>

        <div style={{ marginTop: '1.25rem' }}>
          <button
            type="button"
            onClick={handleCreateHousehold}
            disabled={!canSaveHousehold}
            style={{
              background: canSaveHousehold ? work.tealBlue : work.hairline,
              color: canSaveHousehold ? 'white' : work.muted,
              border: 0, borderRadius: 4, padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.9rem',
              cursor: canSaveHousehold ? 'pointer' : 'not-allowed',
            }}
          >
            {creatingHousehold ? 'Saving…' : <>Continue to Fee<i className="bi bi-chevron-right ms-1" /></>}
          </button>
        </div>
        </>
      )}

      {/* STEP 2: household — name + members with associations */}
      {step === 'household' && (
        <HouseholdStep
          householdName={householdName}
          setHouseholdName={setHouseholdName}
          fscHousehold={fscHousehold}
          onPickFscHousehold={chooseFscHousehold}
          onClearFscHousehold={() => setFscHousehold(null)}
          crmClient={crmClient}
          onPickExisting={chooseExisting}
          onClearCrmClient={() => { setCrmClient(null); setCrmMatchedHousehold(null); setCrmHousehold([]); }}
          crmMatchedHousehold={crmMatchedHousehold}
          crmHousehold={crmHousehold}
          hubspotLead={hubspotLead}
          onClearHubspotLead={() => setHubspotLead(null)}
          members={members}
          addMember={addMember}
          removeMember={removeMember}
          updateMember={updateMember}
          hasPrimary={hasPrimary}
          existingEmails={existingEmails}
          showHubspot={showHubspot}
          canContinue={canContinueHousehold}
          creating={false}
          onContinue={() => setStep('advisor')}
          continueLabel="Continue to Advisor"
        />
      )}

      {/* STEP 2: fee — a single one-time engagement fee */}
      {step === 'fee' && (
        <div style={{ maxWidth: 520 }}>
          {/* Apply discovery fee? — Yes shows the fee card; No continues with no fee. */}
          <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
            <div style={{ fontWeight: 800, color: work.ink, fontSize: '0.95rem', marginBottom: '0.5rem' }}>
              Apply discovery fee?
            </div>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'not-allowed', fontSize: '0.9rem', color: work.muted }}>
                <input type="radio" name="applyFee" checked={applyFee} disabled onChange={() => {}} /> Yes
                <span style={{
                  background: work.hairline, color: work.muted,
                  fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px',
                  padding: '0.1rem 0.4rem', borderRadius: 4,
                }}>
                  Coming soon
                </span>
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem', color: work.ink }}>
                <input type="radio" name="applyFee" checked={!applyFee} onChange={() => { setError(null); setApplyFee(false); }} /> No
              </label>
            </div>
          </div>

          {/* Fee card — only when a discovery fee applies. */}
          {applyFee && (
            <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6, padding: '1.25rem 1.4rem', marginBottom: '1rem' }}>
              <h4 style={{ color: work.ink, fontWeight: 800, margin: '0 0 0.5rem', fontSize: '1.1rem' }}>
                Discovery fee
              </h4>
              <p style={{ color: work.muted, fontSize: '0.875rem', margin: '0 0 1rem' }}>
                One-time engagement fee for {householdName.trim() || 'this household'}. This populates the agreement sent for signature.
              </p>
              <label style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                Fee amount
              </label>
              <div style={{ position: 'relative', maxWidth: 240 }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: work.muted, fontWeight: 700 }}>$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fee}
                  onChange={(e) => setFee(e.target.value)}
                  placeholder="0.00"
                  style={{ width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.5rem 0.7rem 0.5rem 1.4rem', fontSize: '0.95rem', color: work.ink, outline: 'none' }}
                />
              </div>
            </div>
          )}

          <div>
            <button type="button" onClick={handleSaveFeeAndContinue} disabled={savingFee} style={{
              background: work.tealBlue, color: 'white', border: 0, borderRadius: 4,
              padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.9rem', cursor: savingFee ? 'wait' : 'pointer',
            }}>
              {savingFee ? 'Saving…' : (applyFee ? 'Save and continue' : 'Continue')}<i className="bi bi-chevron-right ms-1" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: documents */}
      {step === 'documents' && (
        <DocumentsStep
          catalog={catalog}
          selDocs={selDocs}
          toggleDoc={toggleDoc}
          customList={customList}
          addCustom={addCustom}
          changeCustom={changeCustom}
          removeCustom={removeCustom}
          onContinue={handleSaveDocsAndContinue}
          isMobile={isMobile}
        />
      )}

      {/* STEP 4a: ask about login */}
      {step === 'login-prompt' && (() => {
        // Only members with an email get a login. Tailor the copy to how many
        // will be issued so the operator isn't surprised when name-only members
        // (beneficiaries, dependents…) are skipped.
        const loginCount = loginableMembers.length;
        const nameOnlyCount = created.length - loginCount;
        // Read off wizard state rather than the server: the household was
        // just created with exactly these point people.
        const ccNames = pointPeople.filter(p => p.email).map(p => p.name || p.email);
        return (
          <div style={{
            background: work.surface, border: `1px solid ${work.hairline}`,
            borderRadius: 6, padding: '1.25rem 1.4rem',
          }}>
            <div style={{ color: work.muted, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>
              Documents saved
            </div>
            <h4 style={{ color: work.ink, fontWeight: 800, margin: '0 0 0.5rem', fontSize: '1.1rem' }}>
              Create {loginCount > 1 ? 'logins' : 'a login'} now?
            </h4>
            <p style={{ color: work.muted, fontSize: '0.875rem', margin: '0 0 1rem' }}>
              We&apos;ll generate a temp password for{' '}
              {loginCount === 0
                ? 'the household'
                : loginCount === 1
                  ? <strong>{firstNameOf(loginableMembers[0])}</strong>
                  : <><strong>{loginCount}</strong> members with an email on file</>}
              {' '}and activate {loginCount > 1 ? 'their accounts' : 'the account'} so they can sign in.
              {nameOnlyCount > 0 && (
                <>
                  {' '}
                  <span style={{ color: work.goldenrod }}>
                    {nameOnlyCount} name-only member{nameOnlyCount > 1 ? 's' : ''} (no email) won&apos;t get a login — {firstNameOf(primary)} uploads on their behalf.
                  </span>
                </>
              )}
              {' '}You can always do this later from the client&apos;s detail page.
            </p>
            {loginCount > 0 && (
              <div style={{ marginBottom: '0.85rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: work.deepBlue, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={emailClient} onChange={(e) => setEmailClient(e.target.checked)} />
                  <span><i className="bi bi-envelope me-1" />Also email {loginCount > 1 ? 'each client' : 'the client'} their login (temp password + portal link)</span>
                </label>
                {/* The point people added above are cc'd on this and on every
                    later portal email, and the cc is visible to the client -
                    so name them here while they can still be changed. */}
                {emailClient && ccNames.length > 0 && (
                  <div style={{ color: work.muted, fontSize: '0.75rem', marginTop: 2 }}>
                    cc {ccNames.join(', ')}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button type="button" onClick={handleCreateLogins} disabled={loginCount === 0} style={{
                background: loginCount === 0 ? work.hairline : work.green,
                color: loginCount === 0 ? work.muted : 'white',
                border: 0, borderRadius: 4,
                padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.9rem',
                cursor: loginCount === 0 ? 'not-allowed' : 'pointer',
              }}>
                <i className="bi bi-key me-1" />
                Yes, create login{loginCount > 1 ? 's' : ''}
              </button>
              <button type="button" onClick={skipLogin} style={{
                background: 'transparent', color: work.deepBlue,
                border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
              }}>
                Not yet
              </button>
            </div>
          </div>
        );
      })()}

      {/* STEP 4b: login result */}
      {step === 'login-result' && (
        <div style={{
          background: 'rgba(26,127,78,0.06)', border: `1px solid ${work.green}`,
          borderLeft: `4px solid ${work.green}`, borderRadius: 6,
          padding: '1.25rem 1.4rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <i className="bi bi-check-circle-fill" style={{ color: work.green, fontSize: '1.2rem' }} />
            <h4 style={{ color: work.ink, fontWeight: 800, margin: 0, fontSize: '1.05rem' }}>
              Login{generatedLogins.length > 1 ? 's' : ''} ready
            </h4>
          </div>
          {generatedLogins.map(g => (
            <div key={g.id} style={{ background: 'white', border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.85rem 1rem', marginBottom: '0.75rem' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr auto',
                gap: '0.4rem 0.85rem', alignItems: 'center', fontSize: '0.875rem',
              }}>
                <span style={{ color: work.muted, fontWeight: 600 }}>Username</span>
                <code style={{ background: work.canvas, border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.3rem 0.5rem', color: work.ink }}>
                  {g.email}
                </code>
                <CopyButton value={g.email} label="Copy email" />
                <span style={{ color: work.muted, fontWeight: 600 }}>Temp password</span>
                <code style={{ background: work.canvas, border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.3rem 0.5rem', color: work.ink, letterSpacing: '0.05em', fontWeight: 700 }}>
                  {g.temp_password}
                </code>
                <CopyButton value={g.temp_password} label="Copy password" />
              </div>
              {g.email_sent === true && (
                <p style={{ fontSize: '0.78rem', color: work.green, fontWeight: 600, margin: '0.55rem 0 0' }}>
                  <i className="bi bi-envelope-check me-1" />Emailed to {g.email}.
                </p>
              )}
              {g.email_sent === false && g.email_error && (
                <p style={{ fontSize: '0.78rem', color: work.goldenrod, fontWeight: 600, margin: '0.55rem 0 0' }}>
                  <i className="bi bi-exclamation-triangle me-1" />Couldn&apos;t email ({g.email_error}) — share the password manually.
                </p>
              )}
            </div>
          ))}
          <p style={{ fontSize: '0.78rem', color: work.muted, margin: '0 0 0.85rem' }}>
            <i className="bi bi-info-circle me-1" />
            Only shown once. Leave this page and they&apos;re gone — only the bcrypt hash is on file.
          </p>
          {/* Sharing is deliberately NOT a wizard step — see the note on
              DocumentsStep. This is the hand-off: the client can only be sent
              something once they have a login, which is exactly now. */}
          <p style={{ fontSize: '0.8rem', color: work.deepBlue, margin: '0 0 0.85rem' }}>
            <i className="bi bi-send me-1" />
            Need to send them something? Share documents with this household from
            their page — a welcome packet, a plan, a fee schedule. They&apos;ll be
            emailed when you do.
          </p>
          <button type="button" onClick={finishToDetail} style={{
            background: work.deepBlue, color: 'white', border: 0, borderRadius: 4,
            padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
          }}>
            Done — go to client
          </button>
        </div>
      )}
    </div>
  );
}

// Redtail statuses carry the relationship ("Spouse of Client", "Client
// Beneficiary"), so they give a starting association; the operator can change
// it on the row or in the members list.
const guessAssociation = (status) => {
  const s = (status || '').toLowerCase();
  if (/spouse|wife|husband/.test(s)) return 'spouse';
  if (/partner/.test(s)) return 'domestic_partner';
  if (/child|son\b|daughter/.test(s)) return 'child';
  if (/parent|mother|father/.test(s)) return 'parent';
  if (/dependent/.test(s)) return 'dependent';
  if (/beneficiar/.test(s)) return 'beneficiary';
  return 'other';
};

// Which CRM person in a picked household becomes the portal primary: the
// client, not the spouse or a beneficiary. Falls back to whoever is first.
const NOT_PRIMARY = /spouse|partner|beneficiar|dependent|child|deceased|household \//i;
const pickPrimaryPerson = (people) => {
  const eligible = people.filter(p => !NOT_PRIMARY.test(p.status || ''));
  return eligible.find(p => p.is_client) || eligible[0] || people[0] || null;
};

// The other people in the picked CRM person's household, each addable as a
// member. Already-added people (same first + last name) show as added; a
// deceased person is listed but not addable. An email the household already
// uses (spouses often share one) is left blank, since every portal login
// needs its own.
function CrmHouseholdOffer({ householdName, people, members, existingEmails, onAdd }) {
  const [assoc, setAssoc] = useState({});
  const key = (f, l) => `${(f || '').trim().toLowerCase()}|${(l || '').trim().toLowerCase()}`;
  const added = new Set(members.map(m => key(m.first_name, m.last_name)));
  const usedEmails = new Set([
    ...members.map(m => (m.email || '').trim().toLowerCase()),
    ...existingEmails,
  ].filter(Boolean));
  return (
    <div style={{ background: work.canvas, border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.5rem 0.7rem' }}>
      <div style={{ color: work.muted, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.35rem' }}>
        Also in {householdName || 'this household'}
      </div>
      {people.map(p => {
        const isAdded = added.has(key(p.first_name, p.last_name));
        const deceased = /deceased/i.test(p.status || '');
        const a = assoc[p.id] || guessAssociation(p.status);
        const email = (p.email || '').trim();
        const sharedEmail = email && usedEmails.has(email.toLowerCase());
        return (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', padding: '0.3rem 0', borderTop: `1px solid ${work.hairline}` }}>
            <span style={{ fontWeight: 700, color: work.ink, fontSize: '0.86rem' }}>{p.display_name}</span>
            {p.status && <span style={{ color: work.muted, fontSize: '0.76rem' }}>{p.status}</span>}
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {isAdded ? (
                <span style={{ color: work.green, fontSize: '0.8rem', fontWeight: 700 }}>
                  <i className="bi bi-check2 me-1" />Added
                </span>
              ) : deceased ? (
                <span style={{ color: work.muted, fontSize: '0.78rem' }}>Not added (deceased)</span>
              ) : (
                <>
                  <select
                    value={a}
                    onChange={(e) => setAssoc(prev => ({ ...prev, [p.id]: e.target.value }))}
                    aria-label={`Association for ${p.display_name}`}
                    style={{ border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.2rem 0.35rem', fontSize: '0.8rem', background: 'white', color: work.ink }}
                  >
                    {ASSOCIATIONS.filter(o => o.value !== 'primary').map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => onAdd({
                      first_name: p.first_name, last_name: p.last_name,
                      email: sharedEmail ? '' : email, phone: p.phone, association: a,
                    })}
                    title={sharedEmail ? `${email} is already used in this household - add their own email for a login` : undefined}
                    style={{
                      background: work.goldenrod, color: work.deepBlue, border: 0, borderRadius: 4,
                      padding: '0.25rem 0.7rem', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                    }}
                  >
                    Add
                  </button>
                </>
              )}
            </span>
            {!isAdded && !deceased && sharedEmail && (
              <span style={{ flexBasis: '100%', color: work.muted, fontSize: '0.74rem' }}>
                Shares {email} - added without an email, since each login needs its own.
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
CrmHouseholdOffer.propTypes = {
  householdName: PropTypes.string,
  people: PropTypes.array.isRequired,
  members: PropTypes.array.isRequired,
  existingEmails: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
};

// One picked "existing client" source, with its undo.
function PickedRow({ icon, label, name, detail, actionLabel, onAction }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
      background: work.canvas, border: `1px solid ${work.hairline}`,
      borderRadius: 4, padding: '0.5rem 0.7rem',
    }}>
      <i className={`bi ${icon}`} style={{ color: work.green }} />
      <span style={{ color: work.muted, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </span>
      <span style={{ fontWeight: 700, color: work.ink, fontSize: '0.88rem' }}>{name}</span>
      {detail && <span style={{ color: work.muted, fontSize: '0.78rem' }}>· {detail}</span>}
      <button
        type="button"
        onClick={onAction}
        style={{
          marginLeft: 'auto', background: 'none', border: 0, padding: 0,
          color: work.muted, fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline',
        }}
      >
        {actionLabel}
      </button>
    </div>
  );
}
PickedRow.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  detail: PropTypes.string,
  actionLabel: PropTypes.string.isRequired,
  onAction: PropTypes.func.isRequired,
};

// ─── Household step (name + members) ─────────────────────────────────────────
function HouseholdStep({
  householdName, setHouseholdName, members, addMember, removeMember, updateMember,
  hasPrimary, existingEmails, showHubspot, canContinue, creating, onContinue,
  fscHousehold, onPickFscHousehold, onClearFscHousehold,
  crmClient, onPickExisting, onClearCrmClient, crmMatchedHousehold, crmHousehold = [],
  hubspotLead, onClearHubspotLead,
  continueLabel = 'Continue',
}) {
  const [searching, setSearching] = useState(false);
  // Same person, two answers: the CRM record is matched to one Salesforce
  // household and a different one is linked. Not blocked - the match can be
  // the stale side - but never left unsaid.
  const fscMismatch = Boolean(
    crmClient?.fsc_household_id && fscHousehold && fscHousehold.fsc_id !== crmClient.fsc_household_id,
  );
  const inputStyle = {
    width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4,
    padding: '0.45rem 0.6rem', fontSize: '0.85rem', color: work.ink, outline: 'none',
  };
  const secondaryButton = {
    background: 'transparent', color: work.deepBlue,
    border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
    padding: '0.4rem 0.75rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Existing client? Optional, and skipped for most genuinely new
          clients - but the common reason to be on this page at all is giving
          an existing client portal access. First on the page because either
          search fills in the household name below. Two sources: the Salesforce
          AUM client (a real link, saved with the household) and the merging
          firms' CRMs (prefill only). */}
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`,
        borderTop: `3px solid ${work.tealBlue}`, borderRadius: 6, padding: '1rem 1.15rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <i className="bi bi-person-check" style={{ color: work.tealBlue }} />
          <span style={{ fontWeight: 800, color: work.deepBlue, fontSize: '0.95rem' }}>
            Existing client?
          </span>
          <span style={{
            background: work.hairline, color: work.muted, borderRadius: 4,
            padding: '0.05rem 0.4rem', fontSize: '0.62rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            Optional
          </span>
        </div>

        {(fscHousehold || crmClient || hubspotLead) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.6rem' }}>
            {fscHousehold && (
              <PickedRow
                icon="bi-graph-up-arrow"
                label="Salesforce client"
                name={fscHousehold.name || fscHousehold.fsc_id}
                detail={fscHousehold.rep_name}
                actionLabel="Remove"
                onAction={onClearFscHousehold}
              />
            )}
            {crmClient && crmClient.fsc_household_id && fscHousehold && !fscMismatch && (
              <span style={{ color: work.muted, fontSize: '0.76rem' }}>
                <i className="bi bi-check2 me-1" style={{ color: work.green }} />
                The CRM record and the Salesforce client are the same person in our matching.
              </span>
            )}
            {crmClient && (
              <PickedRow
                icon="bi-journal-text"
                label={crmClient.source_name ? `${crmClient.source_name} CRM` : 'Firm CRM'}
                name={crmClient.display_name}
                detail="details filled in below"
                actionLabel="Clear"
                onAction={onClearCrmClient}
              />
            )}
            {hubspotLead && (
              <PickedRow
                icon="bi-megaphone"
                label="HubSpot lead"
                name={[hubspotLead.first_name, hubspotLead.last_name].filter(Boolean).join(' ') || hubspotLead.email}
                detail={crmClient || fscHousehold ? 'same person, matched on email' : 'details filled in below'}
                actionLabel="Clear"
                onAction={onClearHubspotLead}
              />
            )}
            {crmClient && crmHousehold.length > 0 && (
              <CrmHouseholdOffer
                householdName={crmClient.household_name}
                people={crmHousehold}
                members={members}
                existingEmails={existingEmails}
                onAdd={addMember}
              />
            )}
            {fscMismatch && (
              <div style={{
                background: 'rgba(208,155,44,0.1)', border: `1px solid ${work.goldenrod}`, borderRadius: 4,
                padding: '0.5rem 0.7rem', fontSize: '0.8rem', color: work.ink,
              }}>
                <i className="bi bi-exclamation-triangle-fill me-1" style={{ color: work.goldenrod }} />
                {crmClient.source_name || 'The firm CRM'} matches {crmClient.display_name} to
                {' '}{crmMatchedHousehold ? <strong>{crmMatchedHousehold.name || crmMatchedHousehold.fsc_id}</strong> : 'a different Salesforce household'},
                {' '}not the Salesforce client linked above. Check which is right before continuing.
                {crmMatchedHousehold && (
                  <button
                    type="button"
                    onClick={() => onPickFscHousehold(crmMatchedHousehold)}
                    style={{
                      marginLeft: '0.5rem', background: 'none', border: 0, padding: 0,
                      color: work.tealBlue, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline',
                    }}
                  >
                    Link {crmMatchedHousehold.name || 'that household'} instead
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {searching ? (
          <div style={{ marginTop: '0.6rem' }}>
            <ExistingClientSearch
              showFsc={!fscHousehold}
              showCrm={!crmClient}
              showHubspot={showHubspot && !hasPrimary}
              existingEmails={existingEmails}
              onSelect={(g) => { onPickExisting(g); setSearching(false); }}
              onCancel={() => setSearching(false)}
            />
          </div>
        ) : (!fscHousehold || !crmClient) && (
          <>
            {!fscHousehold && !crmClient && (
              <p style={{ color: work.muted, fontSize: '0.82rem', margin: '0.35rem 0 0.6rem' }}>
                Already a client somewhere at Prosperity, or a lead in HubSpot? Find them and select
                their household - we fill in the name, members and advisor, and link their Salesforce
                record so their AUM and documents show on the client page. Completely new? Skip this.
              </p>
            )}
            <button
              type="button"
              onClick={() => setSearching(true)}
              style={{ ...secondaryButton, marginTop: (fscHousehold || crmClient) ? '0.6rem' : 0 }}
            >
              <i className="bi bi-search me-1" />
              {fscHousehold ? 'Also find them in a firm CRM' : crmClient ? 'Also find their Salesforce client' : 'Find existing client'}
            </button>
          </>
        )}
      </div>

      {/* Household name */}
      <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6, padding: '1rem 1.15rem' }}>
        <label style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
          Household name
        </label>
        <input
          value={householdName}
          onChange={(e) => setHouseholdName(e.target.value)}
          placeholder="e.g. The Smith Family"
          style={{ ...inputStyle, maxWidth: 420 }}
        />
      </div>

      {/* Members list — inline editable */}
      <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ padding: '0.85rem 1.15rem', borderBottom: `1px solid ${work.hairline}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, color: work.ink, fontSize: '0.95rem' }}>Household members</div>
            <div style={{ color: work.muted, fontSize: '0.8rem', marginTop: 2 }}>
              {members.length === 0
                ? 'Add the primary client first, then anyone else in the household.'
                : `${members.length} member${members.length === 1 ? '' : 's'}${hasPrimary ? '' : ' - a primary is still required'}. Edit any field below.`}
            </div>
          </div>
        </div>

        {members.length > 0 && (
          <div>
            {members.map((m) => (
              <div key={m.uid} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.7rem 1.15rem', borderBottom: `1px solid ${work.hairline}`, flexWrap: 'wrap' }}>
                <input value={m.first_name} onChange={(e) => updateMember(m.uid, { first_name: e.target.value })} placeholder="First name" style={{ ...inputStyle, flex: '1 1 120px', minWidth: 110 }} />
                <input value={m.last_name} onChange={(e) => updateMember(m.uid, { last_name: e.target.value })} placeholder="Last name" style={{ ...inputStyle, flex: '1 1 120px', minWidth: 110 }} />
                <input type="email" value={m.email} onChange={(e) => updateMember(m.uid, { email: e.target.value })} placeholder="Email (for login)" style={{ ...inputStyle, flex: '1 1 180px', minWidth: 160 }} />
                <input type="tel" value={m.phone} onChange={(e) => updateMember(m.uid, { phone: e.target.value })} placeholder="Phone" style={{ ...inputStyle, flex: '1 1 130px', minWidth: 120 }} />
                <select value={m.association} onChange={(e) => updateMember(m.uid, { association: e.target.value })} aria-label="Association" style={{ ...inputStyle, flex: '1 1 140px', minWidth: 130, background: 'white', cursor: 'pointer' }}>
                  {ASSOCIATIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
                <button type="button" onClick={() => removeMember(m.uid)} title="Remove member" style={{
                  flexShrink: 0, background: 'transparent', border: `1px solid ${work.hairline}`, borderRadius: 4,
                  padding: '0.4rem 0.55rem', color: work.red, cursor: 'pointer',
                }}>
                  <i className="bi bi-trash" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add-member form (supports HubSpot prefill). Appends an editable row. */}
        <div style={{ padding: '1rem 1.15rem', background: work.canvas }}>
          <AddMemberForm
            hasPrimary={hasPrimary}
            existingEmails={existingEmails}
            onAdd={addMember}
          />
        </div>
      </div>

      {/* Continue sits after the members - it can't be used until there is a
          household name and a primary, so it reads as the last step here. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
        {!canContinue && !creating && (
          <span style={{ color: work.muted, fontSize: '0.8rem' }}>
            {!householdName.trim() ? 'Add a household name to continue.' : 'Add a primary member to continue.'}
          </span>
        )}
        <button type="button" onClick={onContinue} disabled={!canContinue || creating} style={{
          background: (!canContinue || creating) ? work.hairline : work.tealBlue,
          color: (!canContinue || creating) ? work.muted : 'white',
          border: 0, borderRadius: 4, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.9rem',
          cursor: (!canContinue || creating) ? 'not-allowed' : 'pointer',
        }}>
          {creating ? 'Saving…' : <>{continueLabel}<i className="bi bi-chevron-right ms-1" /></>}
        </button>
      </div>
    </div>
  );
}
HouseholdStep.propTypes = {
  continueLabel: PropTypes.string,
  householdName: PropTypes.string.isRequired,
  setHouseholdName: PropTypes.func.isRequired,
  fscHousehold: PropTypes.object,
  onPickFscHousehold: PropTypes.func.isRequired,
  onClearFscHousehold: PropTypes.func.isRequired,
  crmClient: PropTypes.object,
  onPickExisting: PropTypes.func.isRequired,
  onClearCrmClient: PropTypes.func.isRequired,
  crmMatchedHousehold: PropTypes.object,
  crmHousehold: PropTypes.array,
  hubspotLead: PropTypes.object,
  onClearHubspotLead: PropTypes.func,
  members: PropTypes.array.isRequired,
  addMember: PropTypes.func.isRequired,
  removeMember: PropTypes.func.isRequired,
  updateMember: PropTypes.func.isRequired,
  hasPrimary: PropTypes.bool.isRequired,
  existingEmails: PropTypes.instanceOf(Set).isRequired,
  showHubspot: PropTypes.bool,
  canContinue: PropTypes.bool,
  creating: PropTypes.bool,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

// Inline "add a household member" form. Association defaults to primary until a
// primary exists, then to spouse (and primary is removed from the options so a
// household can't have two). Optionally prefills from a HubSpot contact.
function AddMemberForm({ hasPrimary, existingEmails, onAdd }) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [association, setAssociation] = useState(hasPrimary ? 'spouse' : 'primary');
  const [err, setErr] = useState(null);

  useEffect(() => {
    // Keep the association sensible as the household changes underneath us.
    setAssociation(prev => (hasPrimary ? (prev === 'primary' ? 'spouse' : prev) : 'primary'));
  }, [hasPrimary]);

  const inputStyle = {
    border: `1px solid ${work.hairline}`, borderRadius: 4,
    padding: '0.45rem 0.7rem', fontSize: '0.875rem', color: work.ink, outline: 'none',
  };

  const reset = () => {
    setFirst(''); setLast(''); setEmail(''); setPhone('');
    setAssociation(hasPrimary ? 'spouse' : 'primary');
  };

  const submit = (e) => {
    e.preventDefault();
    setErr(null);
    const f = first.trim(); const l = last.trim(); const em = email.trim().toLowerCase();
    if (!f && !l) { setErr('Enter a first or last name.'); return; }
    if (em && !em.includes('@')) { setErr("That doesn't look like a valid email."); return; }
    if (em && existingEmails.has(em)) { setErr('A client account already exists for that email.'); return; }
    onAdd({ first_name: f, last_name: l, email: em, phone: phone.trim(), association });
    reset();
  };

  const options = ASSOCIATIONS.filter(a => a.value !== 'primary' || !hasPrimary || association === 'primary');

  return (
    <form onSubmit={submit}>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <input type="text" value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" style={{ ...inputStyle, flex: '1 1 140px', minWidth: 130 }} />
        <input type="text" value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last name" style={{ ...inputStyle, flex: '1 1 140px', minWidth: 130 }} />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (for login)" style={{ ...inputStyle, flex: '1 1 200px', minWidth: 180 }} />
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" style={{ ...inputStyle, flex: '1 1 150px', minWidth: 140 }} />
        <select value={association} onChange={(e) => setAssociation(e.target.value)} aria-label="Association" style={{ ...inputStyle, flex: '1 1 150px', minWidth: 140, background: 'white', cursor: 'pointer' }}>
          {options.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <button type="submit" style={{
          background: work.goldenrod, color: work.deepBlue, border: 0, borderRadius: 4,
          padding: '0.45rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap',
        }}>
          <i className="bi bi-person-plus me-1" />Add {!hasPrimary ? 'primary' : 'member'}
        </button>
      </div>
      {err && <div style={{ color: work.red, fontSize: '0.8rem', marginTop: '0.4rem' }}>{err}</div>}
    </form>
  );
}
AddMemberForm.propTypes = {
  hasPrimary: PropTypes.bool.isRequired,
  existingEmails: PropTypes.instanceOf(Set).isRequired,
  onAdd: PropTypes.func.isRequired,
};


// ─── Documents step (extracted for readability) ──────────────────────────────
// Requesting documents FROM the household. There is deliberately no matching
// "share documents with them" step in this wizard, and the reason is the
// bucket rather than the layout:
//
//   * pca-client-documents is Compliance-mode WORM with a 7-year lock. An
//     upload cannot be undone by anyone, including us.
//   * This wizard hard-deletes clients when you go Back and remove a member
//     (see deleteClient above). A file shared mid-wizard and then abandoned
//     would be permanently retained against a household that no longer
//     exists — an orphan nobody can clear.
//   * And the household has no login until the step AFTER this one, so
//     nothing shared here could be delivered or announced anyway.
//
// So sharing stays on the client's own page, where the household is real and
// settled, and the wizard's last step points at it instead.
function DocumentsStep({
  catalog, selDocs, toggleDoc,
  customList, addCustom, changeCustom, removeCustom,
  onContinue, onBack, isMobile,
}) {
  const total = selDocs.size;
  // Custom requests only count once they actually have text — empty rows
  // shouldn't read as a real ask.
  const customCount = customList.filter(c => c.label.trim().length > 0).length;
  const hasCustom = customCount > 0;
  // Requesting nothing is a legitimate setup, not an incomplete one: since the
  // portal can deliver documents TO a household, a client can exist purely to
  // receive. This step used to block on an empty list, which made those
  // clients unsetuppable. The button changes wording instead of greying out,
  // so continuing with nothing is visibly a choice rather than a slip.
  const nothingRequested = total === 0 && !hasCustom;

  return (
    <div style={{
      background: work.surface, border: `1px solid ${work.hairline}`,
      borderRadius: 6, overflow: 'hidden',
    }}>
      <div style={{
        padding: '0.85rem 1rem', borderBottom: `1px solid ${work.hairline}`,
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: work.ink, fontSize: '0.95rem' }}>
            Requested documents
          </div>
          <div style={{ color: work.muted, fontSize: '0.8rem', marginTop: 2 }}>
            {nothingRequested ? (
              <>Check anything you need from this household — or skip, if you&apos;re
              setting them up to receive documents from us. You can request items
              any time from their page.</>
            ) : (
              <>Check every item we need from this household. <strong>{total}</strong> selected
              {customCount > 0 && ` + ${customCount} custom`}. The household&apos;s primary uploads for everyone.</>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {onBack && (
            <button type="button" onClick={onBack} style={{
              background: 'transparent', color: work.deepBlue, border: `1px solid ${work.hairlineStrong}`,
              borderRadius: 4, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
            }}>
              <i className="bi bi-chevron-left me-1" />Back
            </button>
          )}
          <button type="button" onClick={onContinue} style={{
            background: nothingRequested ? 'transparent' : work.tealBlue,
            color: nothingRequested ? work.deepBlue : 'white',
            border: nothingRequested ? `1px solid ${work.hairlineStrong}` : 0,
            borderRadius: 4,
            padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.82rem',
            cursor: 'pointer',
          }}>
            {nothingRequested ? 'Skip for now' : 'Save and continue'}
            <i className="bi bi-chevron-right ms-1" />
          </button>
        </div>
      </div>

      {/* Custom requests — sit above the catalog so ad-hoc asks are reached
          first. Add as many as needed; the client sees each verbatim alongside
          the checklist items, and each is tracked/uploaded separately. */}
      <div style={{
        padding: '0.9rem 1rem', borderBottom: `1px solid ${work.hairline}`,
        background: hasCustom ? 'rgba(208,155,44,0.06)' : work.surface,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
          <span>
            <span style={{ fontWeight: 700, color: work.ink, fontSize: '0.9rem' }}>
              Custom requests
            </span>
            <span style={{ display: 'block', color: work.muted, fontSize: '0.8rem', marginTop: 1 }}>
              Ask for anything not in the catalog below — add one row per document.
            </span>
          </span>
          <button type="button" onClick={addCustom} style={{
            flexShrink: 0, background: 'transparent', border: `1px solid ${work.hairlineStrong}`,
            borderRadius: 4, padding: '0.35rem 0.6rem', fontSize: '0.8rem', fontWeight: 700,
            color: work.deepBlue, cursor: 'pointer',
          }}>
            <i className="bi bi-plus-lg me-1" />Add request
          </button>
        </div>
        {customList.length > 0 && (
          <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {customList.map((c, i) => (
              <div key={c.uid} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <textarea
                  value={c.label}
                  onChange={(e) => changeCustom(c.uid, e.target.value)}
                  placeholder={i === 0 ? 'e.g. Trust amendment signed in 2023' : 'Describe another document to upload'}
                  rows={2}
                  style={{
                    flex: 1, border: `1px solid ${work.hairline}`, borderRadius: 4,
                    padding: '0.5rem 0.7rem', fontSize: '0.875rem',
                    color: work.ink, outline: 'none', resize: 'vertical', fontFamily: 'inherit',
                  }}
                />
                <button type="button" onClick={() => removeCustom(c.uid)} title="Remove this request" style={{
                  flexShrink: 0, background: 'transparent', border: `1px solid ${work.hairline}`,
                  borderRadius: 4, padding: '0.5rem 0.6rem', color: work.red, cursor: 'pointer',
                }}>
                  <i className="bi bi-trash" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {catalog.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: work.muted }}>Loading catalog…</div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(360px, 1fr))',
          gap: 0,
        }}>
          {catalog.map(cat => (
            <div key={cat.category} style={{
              borderRight: isMobile ? 'none' : `1px solid ${work.hairline}`,
              borderBottom: `1px solid ${work.hairline}`,
              padding: '0.85rem 1rem',
            }}>
              <div style={{ color: work.tealBlue, fontWeight: 800, fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                {cat.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {(cat.items || []).map(item => {
                  const checked = selDocs.has(item.key);
                  return (
                    <label key={item.key} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                      cursor: 'pointer', padding: '0.25rem 0.3rem', borderRadius: 4,
                      background: checked ? 'rgba(0,105,166,0.06)' : 'transparent',
                    }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleDoc(item.key)} style={{ marginTop: 3 }} />
                      <span style={{ color: work.ink, fontSize: '0.85rem', lineHeight: 1.35 }}>{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
DocumentsStep.propTypes = {
  catalog: PropTypes.array.isRequired,
  selDocs: PropTypes.instanceOf(Set).isRequired,
  toggleDoc: PropTypes.func.isRequired,
  customList: PropTypes.array.isRequired,
  addCustom: PropTypes.func.isRequired,
  changeCustom: PropTypes.func.isRequired,
  removeCustom: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  isMobile: PropTypes.bool,
};
